import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Send, Loader2, Volume2, VolumeX } from 'lucide-react';
import { BACKEND_URL as backendUrl } from '@/config/api';
import { Link } from 'react-router-dom';

// Helper function to render response text with inline hyperlinks
const renderResponseWithLinks = (text) => {
  if (!text) return null;
  
  // Define link mappings (case-insensitive patterns)
  const linkPatterns = [
    { pattern: /\bPricing page\b/gi, url: '/pricing', label: 'Pricing page' },
    { pattern: /\bHelp Center\b/gi, url: '/help', label: 'Help Center' },
    { pattern: /\bcontact support\b/gi, url: '/support', label: 'contact support' },
    { pattern: /\bSupport\b/g, url: '/support', label: 'Support' }, // Case-sensitive to avoid matching "support" in other contexts
  ];
  
  // Build a combined regex to split text
  const combinedPattern = /(\bPricing page\b|\bHelp Center\b|\bcontact support\b|\bSupport\b)/gi;
  const parts = text.split(combinedPattern);
  
  return parts.map((part, index) => {
    // Check if this part matches any of our link patterns
    const lowerPart = part.toLowerCase();
    if (lowerPart === 'pricing page') {
      return <Link key={index} to="/pricing" className="text-purple-400 hover:text-purple-300 underline">{part}</Link>;
    }
    if (lowerPart === 'help center') {
      return <Link key={index} to="/help" className="text-purple-400 hover:text-purple-300 underline">{part}</Link>;
    }
    if (lowerPart === 'contact support') {
      return <Link key={index} to="/support" className="text-purple-400 hover:text-purple-300 underline">{part}</Link>;
    }
    if (part === 'Support') { // Case-sensitive match
      return <Link key={index} to="/support" className="text-purple-400 hover:text-purple-300 underline">{part}</Link>;
    }
    return part;
  });
};

const AskAudiaModal = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTTSLoading, setIsTTSLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [hasPlayedWelcome, setHasPlayedWelcome] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const messagesEndRef = useRef(null);
  const lastAssistantRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);

  // TTS welcome uses plain text (no HTML), display welcome uses JSX with superscript
  const welcomeMessageTTS = "Hi, I'm Audia. If you're wondering what NewAudia is, how it works, or why it's different — ask me anything.";

  // Scroll to top of latest assistant response, or bottom for user messages
  useEffect(() => {
    if (messages.length === 0) return;
    const lastMsg = messages[messages.length - 1];
    if (lastMsg.role === 'assistant' && lastAssistantRef.current) {
      lastAssistantRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Play TTS for text
  const playTTS = useCallback(async (text) => {
    if (!audioEnabled) return;
    
    setIsTTSLoading(true);
    try {
      const response = await fetch(`${backendUrl}/api/ask-audia/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text })
      });

      if (!response.ok) throw new Error('TTS failed');

      const data = await response.json();
      
      if (data.audio_base64 && data.format) {
        const audioSrc = `data:audio/${data.format};base64,${data.audio_base64}`;
        
        if (audioRef.current) {
          audioRef.current.pause();
        }
        
        const audio = new Audio(audioSrc);
        audioRef.current = audio;
        
        audio.play().catch(err => {
          console.log('Audio autoplay blocked:', err);
        });
      }
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      setIsTTSLoading(false);
    }
  }, [audioEnabled]);

  // Play welcome message when modal opens
  useEffect(() => {
    if (isOpen && !hasPlayedWelcome && audioEnabled && messages.length === 0) {
      const timer = setTimeout(() => {
        playTTS(welcomeMessageTTS);
        setHasPlayedWelcome(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen, hasPlayedWelcome, audioEnabled, messages.length, playTTS]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    }
  }, [isOpen]);

  const popularQuestions = [
    "Is this an audiobook?",
    "How is it different?",
    "Can I try it free?",
    "How do authors earn?"
  ];

  const sendMessage = async (question) => {
    if (!question.trim() || isLoading) return;

    // Stop any currently playing audio before sending new question
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }

    const userMessage = { role: 'user', content: question.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setSuggestions([]);
    setIsLoading(true);

    try {
      const response = await fetch(`${backendUrl}/api/ask-audia`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: question.trim(),
          session_id: sessionId
        })
      });

      if (!response.ok) throw new Error('Failed to get response');

      const data = await response.json();
      
      if (data.session_id) {
        setSessionId(data.session_id);
      }

      const assistantMessage = { role: 'assistant', content: data.response };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Update suggestions
      if (data.suggestions?.length > 0) {
        setSuggestions(data.suggestions);
      } else {
        setSuggestions([]);
      }
      
      // Play TTS for the response
      if (audioEnabled && data.response) {
        playTTS(data.response);
      }
    } catch (error) {
      console.error('Ask Audia error:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: "I'm having a moment. Please try again, or feel free to explore the site—you can always come back and ask me anything." 
      };
      setMessages(prev => [...prev, errorMessage]);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleQuestionClick = (question) => {
    sendMessage(question);
  };

  const handleSuggestionTap = (question) => {
    if (isLoading) return;
    sendMessage(question);
  };

  const handleCloseConversation = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setMessages([]);
    setSessionId(null);
    setHasPlayedWelcome(false);
    setSuggestions([]);
    onClose();
  };

  const toggleAudio = () => {
    if (audioRef.current && audioEnabled) {
      audioRef.current.pause();
    }
    setAudioEnabled(!audioEnabled);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full sm:max-w-lg sm:mx-4 h-[100dvh] sm:h-auto sm:max-h-[80vh] bg-gradient-to-br from-slate-900 to-slate-950 border-0 sm:border sm:border-purple-500/30 sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-700/50 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src="/audia-avatar.jpg" alt="Audia" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border border-purple-500/50" />
            <div>
              <h3 className="text-base sm:text-xl font-bold text-white">Ask Audia</h3>
              <p className="text-xs text-purple-400 hidden sm:block">Your guide to NewAudia<sup className="text-[8px]">™</sup></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={toggleAudio}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                audioEnabled ? 'bg-purple-600 hover:bg-purple-500' : 'bg-slate-800 hover:bg-slate-700'
              }`}
              title={audioEnabled ? 'Mute Audia' : 'Unmute Audia'}
            >
              {audioEnabled ? (
                <Volume2 className="w-4 h-4 text-white" />
              ) : (
                <VolumeX className="w-4 h-4 text-gray-400" />
              )}
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* TTS Loading Indicator */}
        {isTTSLoading && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-purple-600/90 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1.5 z-10">
            <Volume2 className="w-3 h-3 animate-pulse" />
            <span>Generating audio...</span>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 min-h-[200px]">
          {messages.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm sm:text-base text-gray-300 mb-4 leading-relaxed">
                Hi, I'm <strong className="text-purple-400">Audia</strong>.<br />
                If you're wondering what NewAudia<sup className="text-[8px]">™</sup> is, how it works, or why it's different — ask me anything.
              </p>
              <div className="space-y-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Popular questions</p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {popularQuestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleQuestionClick(q)}
                      data-testid={`landing-audia-popular-${i}`}
                      className="px-2.5 py-1 sm:px-3 sm:py-1.5 bg-slate-800/70 border border-slate-700/50 rounded-full text-[11px] sm:text-sm text-gray-400 hover:text-white hover:border-purple-500/50 transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, index) => (
                <div
                  key={index}
                  ref={msg.role === 'assistant' && index === messages.length - 1 ? lastAssistantRef : null}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                      msg.role === 'user'
                        ? 'bg-purple-600 text-white'
                        : 'bg-slate-800/80 text-gray-200 border border-slate-700/50'
                    }`}
                  >
                    <p className="text-sm sm:text-base whitespace-pre-wrap">
                      {msg.role === 'assistant' ? renderResponseWithLinks(msg.content) : msg.content}
                    </p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-slate-800/80 border border-slate-700/50 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 text-purple-400 animate-spin" />
                      <span className="text-sm text-gray-400">Audia is thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Suggestion Chips */}
        {suggestions.length > 0 && !isLoading && (
          <div data-testid="landing-audia-suggestions" className="px-3 pt-2 pb-1 border-t border-slate-700/50 bg-slate-900/60 flex-shrink-0">
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((q, idx) => (
                <button
                  key={idx}
                  data-testid={`landing-audia-suggestion-${idx}`}
                  onClick={() => handleSuggestionTap(q)}
                  className="px-2.5 py-1 sm:px-3 sm:py-1.5 text-[11px] sm:text-xs text-slate-200 bg-slate-800 border border-slate-600/60 rounded-full hover:bg-purple-900/40 hover:border-purple-500/50 hover:text-purple-300 active:scale-95 transition-all duration-150 cursor-pointer leading-snug"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="flex-shrink-0 px-4 sm:px-6 pb-4 pt-2 border-t border-slate-700/50">
          <form onSubmit={handleSubmit} className="flex gap-2 mb-3">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask me anything about NewAudia™..."
              className="flex-1 bg-slate-800/50 border border-slate-700/50 rounded-full px-4 py-2.5 text-sm sm:text-base text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
              disabled={isLoading}
              data-testid="landing-audia-input"
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="w-10 h-10 sm:w-11 sm:h-11 bg-purple-600 hover:bg-purple-500 disabled:bg-slate-700 disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-colors"
              data-testid="landing-audia-send-btn"
            >
              <Send className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </button>
          </form>
          
          <button
            onClick={handleCloseConversation}
            className="w-full py-2.5 text-sm text-gray-400 hover:text-white bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 rounded-xl transition-colors"
            data-testid="landing-audia-close-btn"
          >
            Close Conversation
          </button>
        </div>
      </div>
    </div>
  );
};

export default AskAudiaModal;
